// Third-party imports
import { useRef } from 'react';
import {
  TextInput,
  SelectInput,
  AutocompleteArrayInput,
  NumberInput,
  useEditContext,
  useGetOne,
  required,
  TabbedForm,
  FormTab,
} from 'react-admin';
import { Typography, Divider, Box, Chip } from '@mui/material';

// Local imports - types
import type { ValidCodeNameType } from '@src/types';
import type { PartnerSettings } from '@src/settings';

// Local imports - components
import CatalogEditToolbar from './CatalogEditToolbar';
import VariantsSection from './VariantsSection';
import ImagesSection from './ImagesSection';
import AvailabilityInput from './AvailabilityInput';
import type { VariantSaveRef } from './VariantDetailPanel';

// Form component that can access the record through Edit context
const CatalogForm = () => {
  const { record } = useEditContext();
  const attributes = record?.attributeData;
  // Shared ref that lets the Variants tab register its save handler so the
  // toolbar's SaveButton can trigger a variant save (with optional impact
  // confirmation) instead of the default parent-form save. The ref is null
  // when no variant is selected or when a non-Variants tab is active.
  const variantSaveRef: VariantSaveRef = useRef(null);

  const { data: settings } = useGetOne<PartnerSettings & { id: number }>('settings', { id: 0 });
  const currencyCode = settings?.primaryCurrencyCode || 'USD';

  // Create choices for select inputs
  const departmentChoices = attributes?.validDepartments?.map((dept: string) => ({
    id: dept,
    name: dept,
  })) || [];

  const categoryChoices = attributes?.validCategories?.map((cat: string) => ({
    id: cat,
    name: cat,
  })) || [];

  const subcategoryChoices = attributes?.validSubcategories?.map((subcat: string) => ({
    id: subcat,
    name: subcat,
  })) || [];

  const genderChoices = attributes?.validGenders?.map((gender: ValidCodeNameType) => ({
    id: gender.code,
    name: gender.name,
  })) || [];

  const rubricCodeChoices = attributes?.validRubricCodes?.map((code: string) => ({
    id: code,
    name: code,
  })) || [];

  return (
    <>
      {record?.id && (
        <Box sx={{ px: 2, pt: 2 }}>
          <Chip
            label={`Parent SKU: ${record.id}`}
            variant="outlined"
            size="medium"
          />
        </Box>
      )}
      <TabbedForm toolbar={<CatalogEditToolbar variantSaveRef={variantSaveRef} />}>
        <FormTab label="Parent Details">
          {/* Basic Information */}
          <Typography variant="h6" gutterBottom sx={{ width: '100%' }}>
            Basic Information
          </Typography>
          <TextInput
            source="productName"
            label="Product Name"
            fullWidth
            validate={required()}
          />
          <TextInput
            source="productDescription"
            label="Product Description"
            fullWidth
            multiline
            rows={4}
          />
          <TextInput
            source="brandDisplay"
            label="Brand Display"
            fullWidth
          />

          <Divider sx={{ my: 3, width: '100%' }} />

          {/* Categorization */}
          <Typography variant="h6" gutterBottom sx={{ width: '100%' }}>
            Categorization
          </Typography>
          <AutocompleteArrayInput
            source="departments"
            label="Departments"
            choices={departmentChoices}
            fullWidth
          />
          <AutocompleteArrayInput
            source="categories"
            label="Categories"
            choices={categoryChoices}
            fullWidth
          />
          <AutocompleteArrayInput
            source="subcategories"
            label="Subcategories"
            choices={subcategoryChoices}
            fullWidth
          />
          <SelectInput
            source="gender.code"
            label="Gender"
            choices={genderChoices}
            optionText="name"
            optionValue="id"
            fullWidth
          />

          <Divider sx={{ my: 3, width: '100%' }} />

          {/* Pricing & Eligibility */}
          <Typography variant="h6" gutterBottom sx={{ width: '100%' }}>
            Pricing & Eligibility
          </Typography>
          <NumberInput
            source="msrpPrice"
            label={`MSRP/RRP (${currencyCode})`}
            step={0.01}
            min={0}
            fullWidth
          />
          <NumberInput
            source="lastKnownRetailPrice"
            label={`Last Known Price (${currencyCode})`}
            step={0.01}
            min={0}
            fullWidth
          />
          <TextInput
            source="taxCode"
            label="Tax Code"
            fullWidth
          />
          <AvailabilityInput
            source="availability"
            label="Eligibility"
          />
          <SelectInput
            source="rubricCode"
            label="Inspection Category (Rubric Code)"
            choices={rubricCodeChoices}
            optionText="name"
            optionValue="id"
            fullWidth
          />
        </FormTab>

        <FormTab label="Images">
          <ImagesSection />
        </FormTab>

        <FormTab label="Variants">
          <VariantsSection variantSaveRef={variantSaveRef} />
        </FormTab>
      </TabbedForm>
    </>
  );
};

export default CatalogForm;
